import { HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { PureAbility } from '@casl/ability';
import { NgbTooltip, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { L10nIntlModule, L10nLoader, L10nTranslationModule } from 'angular-l10n';
import { ToastrModule } from 'ngx-toastr';

import { APPSETTINGS_CONFIG, ENVIRONMENT_CONFIG } from '@orgacard/common';
import { MWSSystemLoginModule } from '@orgacard/login';
import { OCTestIdConfig, OCTestIdDirective } from '@orgacard/testid';

import { CastPipeModule } from '@pipes';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { EnvironmentService } from './core/environment.service';
import { HttpInterceptorsProvider } from './http-interceptors';
import { KontoComponent } from './konto/konto.component';
import { initL10n, l10config } from './l10config';
import { LayoutModule } from './layout/layout.module';
import { LoadingOverlayComponent } from './loading-overlay/loading-overlay.component';
import { AppAbility, resolveAction } from './login/appAbility';
import { LoginComponent } from './login/login.component';
import {
    BASE_PATH as GOODSRECEIPT_BASE_PATH,
    Configuration as GoodsReceiptApiConfiguration,
    ApiModule as GoodsReceiptApiModule,
} from './mwsp/goodsreceipt/api';
import { BASE_PATH as PURCHASE_BASE_PATH, Configuration as PurchaseApiConfiguration, ApiModule as PurchaseApiModule } from './mwsp/purchase/api';
import { BASE_PATH as RECIPESHOP_BASE_PATH, Configuration as RecipeshopApiConfiguration, ApiModule as RecipeshopApiModule } from './mwsp/recipeshop/api';
import { SharedModule } from './mwsp/shared/shared.module';
import { BASE_PATH as WAREHOUSES_BASE_PATH, Configuration as WarehousesApiConfiguration, ApiModule as WarehousesApiModule } from './mwsp/warehouses/api';
import { I18nPipeModule } from './pipes/i18n/i18n-pipe.module';
import { SystemconfigComponent } from './systemconfig/systemconfig.component';

export const initializeApp =
    (http: HttpClient, envService: EnvironmentService): (() => Promise<any>) =>
    () =>
        firstValueFrom(
            http.get('/assets/config.json').pipe(
                tap((config: any) => {
                    // FrontEnd-Applikation läuft in eigenem nginx-Container.
                    // BackEnd-Zielhost-URL muss via config.json konfiguriert werden!
                    // console.log(config);
                    envService.isKubernetes = true;
                    envService.appSettings.protocol =
                        config.apiHost && config.apiHost.length > 0 ? config.apiHost.substr(0, config.apiHost.indexOf('://') + 3) : window.location.protocol;
                    envService.appSettings.location =
                        config.apiHost && config.apiHost.length > 0 ? config.apiHost.substr(config.apiHost.indexOf('://') + 3) : window.location.hostname;
                    envService.appSettings.ctxroot = '/rest';
                    envService.ctxrootStage = config.stageHost + '/rest';
                    envService.ctxrootDashboard = config.dashboardHost;
                }),
                catchError((err) => {
                    // config.json wurde nicht gefunden.
                    // FrondEnd-Applikation ist Bestandteil des BackEnd-WARs!
                    // BackEnd-Zielhost-URL wird in CI-Prozess via environment.ts gesteuert!
                    envService.isKubernetes = false;
                    envService.appSettings.protocol = window.location.protocol;
                    envService.appSettings.location = window.location.hostname;
                    if (environment.port) {
                        if (window.location.port.startsWith('42')) {
                            envService.appSettings.location = environment.endpoint + ':' + environment.port;
                        } else {
                            envService.appSettings.location = envService.appSettings.location + ':' + environment.port;
                        }
                    } else if (window.location.port !== '' && window.location.port !== '80' && window.location.port !== '443') {
                        envService.appSettings.location = envService.appSettings.location + ':' + window.location.port;
                    }
                    envService.appSettings.ctxroot = '/mws/rest';
                    if (window.location.port.startsWith('42')) {
                        envService.ctxrootStage = `${window.location.protocol}//${environment.endpoint}:${environment.stagePort}/mwsstage/rest`;
                    } else {
                        envService.ctxrootStage = window.location.protocol + '//' + window.location.hostname + '/mwsstage/rest';
                    }
                    envService.ctxrootDashboard = 'https://01.dashboards.mws-dev-01.d.orgacard.de';
                    return of({});
                }),
            ),
        );
@NgModule({
    declarations: [
        AppComponent,
        // Pages
        LoginComponent,
        //Page2Component,
        LoadingOverlayComponent,
        KontoComponent,
        SystemconfigComponent,
    ],
    exports: [KontoComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot(),
        L10nTranslationModule.forRoot(l10config),
        L10nIntlModule,
        // App
        AppRoutingModule,
        LayoutModule,
        // OC
        // LoginModule,
        // AbilityModule,
        CastPipeModule,
        PurchaseApiModule.forRoot(
            () =>
                new PurchaseApiConfiguration({
                    // basePath: (config.apiHost && config.apiHost.length>0) ? config.apiHost : environment.purchaseEndpoint,
                }),
        ),
        GoodsReceiptApiModule.forRoot(
            () =>
                new GoodsReceiptApiConfiguration({
                    // basePath: (config.apiHost && config.apiHost.length>0) ? config.apiHost : environment.goodsReceiptEndpoint,
                }),
        ),
        WarehousesApiModule.forRoot(
            () =>
                new WarehousesApiConfiguration({
                    // basePath: (config.apiHost && config.apiHost.length>0) ? config.apiHost : environment.warehousesEndpoint,
                }),
        ),
        RecipeshopApiModule.forRoot(
            () =>
                new RecipeshopApiConfiguration({
                    // basePath: (config.apiHost && config.apiHost.length>0) ? config.apiHost : environment.recipeshopEndpoint,
                }),
        ),
        MWSSystemLoginModule,
        I18nPipeModule,

        OCTestIdDirective,
        NgbTooltip,
        SharedModule,
    ],
    providers: [
        Title,
        AppService,
        HttpInterceptorsProvider,
        { provide: OCTestIdConfig, useValue: { enabled: environment.removeDataTestId } },
        { provide: LOCALE_ID, useValue: 'de' },
        { provide: APPSETTINGS_CONFIG, useValue: {} },
        { provide: ENVIRONMENT_CONFIG, useValue: environment },
        { provide: AppAbility, useValue: new AppAbility([], { resolveAction }) },
        { provide: PureAbility, useExisting: AppAbility },
        {
            provide: PURCHASE_BASE_PATH,
            useFactory: (envService: EnvironmentService) =>
                envService.isKubernetes ? envService.appSettings.protocol + '//' + envService.appSettings.location : environment.purchaseEndpoint,
            deps: [EnvironmentService],
        },
        {
            provide: GOODSRECEIPT_BASE_PATH,
            useFactory: (envService: EnvironmentService) =>
                envService.isKubernetes ? envService.appSettings.protocol + '//' + envService.appSettings.location : environment.goodsReceiptEndpoint,
            deps: [EnvironmentService],
        },
        {
            provide: WAREHOUSES_BASE_PATH,
            useFactory: (envService: EnvironmentService) =>
                envService.isKubernetes ? envService.appSettings.protocol + '//' + envService.appSettings.location : environment.warehousesEndpoint,
            deps: [EnvironmentService],
        },
        {
            provide: RECIPESHOP_BASE_PATH,
            useFactory: (envService: EnvironmentService) =>
                envService.isKubernetes ? envService.appSettings.protocol + '//' + envService.appSettings.location : environment.recipeshopEndpoint,
            deps: [EnvironmentService],
        },
        provideAppInitializer(() => {
            const initializerFn = initL10n(inject(L10nLoader));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeApp(inject(HttpClient), inject(EnvironmentService));
            return initializerFn();
        }),
        provideHttpClient(
            withInterceptorsFromDi(), //
            withFetch(),
        ),
    ],
})
export class AppModule {
    constructor() {
        const conf = inject(NgbTooltipConfig);

        // Customize default values of tooltips used by this module
        conf.openDelay = 1000;
        conf.closeDelay = 50;
        conf.triggers = 'hover';
        conf.container = 'body';
    }
}
